/**
 * Where the run that just ended puts you, per window.
 *
 * The death panel's half of the player report: "we should show an hourly, daily, weekly, monthly
 * and overall rating. That might be too much for mobile so trim it down." Five periods of one
 * metric, which is exactly the shape `leaderboard.standing` answers in — one call, fanned out
 * over boards the API process is already caching, and **not a second ranking path**.
 *
 * ## A guest is told why, not shown nothing
 *
 * Guests are excluded from these aggregations by design: a guest row is reclaimed by a device id
 * anybody can send, so it is not an identity to put on a world board. The server answers a guest
 * with `viewer: null` — but this component does not even ask, because the session already says
 * so, and a request that can only come back empty is a request worth not making. What a guest
 * gets instead is the one sentence that would change it, and a link to the page that does.
 *
 * ## The trim is CSS, not a second render path
 *
 * Every period is in the DOM; the ones past `LEADERBOARD_STANDING_COMPACT` are `hidden sm:flex`,
 * the arrangement `StatsPanel` and the HUD leaderboard are already under. All time is never
 * dropped — it is the row a player recognises themselves in — so the compact set is the first
 * two windows plus the last one.
 */

import {
  LEADERBOARD_STANDING_COMPACT,
  LEADERBOARD_STANDING_PERIODS,
  type LeaderboardMetric,
  type LeaderboardPeriod,
} from '@chompdome/shared/constants';
import { useSession } from '@/hooks/use-session';
import { METRIC_LABELS, PERIOD_LABELS } from '@/lib/leaderboards';
import { trpc } from '@/lib/trpc';

/**
 * Which board the death panel reports against.
 *
 * The biggest single run — the number the whole screen is already about, since the panel's
 * headline figure is the mass this run peaked at. Ranking a player by anything else here would
 * be answering a question they did not just ask.
 */
const STANDING_METRIC: LeaderboardMetric = 'mass';

export interface RunStandingProps {
  className?: string;
}

export function RunStanding({ className = '' }: RunStandingProps) {
  const { user } = useSession();
  // A guest and a signed-out reader are the same case, and neither is asked about: the server
  // would answer `viewer: null` and the round trip would buy the panel nothing.
  const ranked = user !== null && !user.isGuest;

  const standing = trpc.leaderboard.standing.useQuery(
    { metric: STANDING_METRIC, periods: [...LEADERBOARD_STANDING_PERIODS] },
    { enabled: ranked },
  );

  if (!ranked) return <RegisterPrompt className={className} />;

  const byPeriod = new Map(standing.data?.periods.map((row) => [row.period, row]) ?? []);

  return (
    <section
      data-testid="run-standing"
      aria-label="Your ranking"
      className={`rounded-lg bg-secondary/60 p-2 text-left ${className}`}
    >
      <h3 className="px-1 pb-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {METRIC_LABELS[STANDING_METRIC].short} ranking
      </h3>
      <dl>
        {LEADERBOARD_STANDING_PERIODS.map((period, index) => (
          <StandingRow
            key={period}
            period={period}
            row={byPeriod.get(period) ?? null}
            loading={standing.isLoading}
            // The windows between the first two and all time are what a phone gives up. All time
            // is the last entry and is never one of them.
            compactHidden={
              index >= LEADERBOARD_STANDING_COMPACT &&
              index < LEADERBOARD_STANDING_PERIODS.length - 1
            }
          />
        ))}
      </dl>
    </section>
  );
}

interface PeriodStanding {
  period: LeaderboardPeriod;
  rank: number | null;
  value: number;
  ranked: number;
}

/**
 * One window.
 *
 * A null rank is not an error and is not "last": it means this player is outside the depth the
 * board ranks to, or has finished nothing inside the window — so the row says "unranked" and
 * still prints the number they hold, which is the honest half of the answer.
 */
function StandingRow({
  period,
  row,
  loading,
  compactHidden,
}: {
  period: LeaderboardPeriod;
  row: PeriodStanding | null;
  loading: boolean;
  compactHidden: boolean;
}) {
  const place =
    row === null
      ? loading
        ? '…'
        : '—'
      : row.rank === null
        ? 'Unranked'
        : `#${row.rank.toLocaleString()}`;

  return (
    <div
      data-testid={`standing-${period}`}
      className={`${compactHidden ? 'hidden sm:flex' : 'flex'} items-baseline justify-between gap-2 rounded px-1 py-0.5 text-[11px] sm:text-xs`}
    >
      <dt className="text-muted-foreground">{PERIOD_LABELS[period]}</dt>
      <dd className="flex items-baseline gap-1.5 tabular-nums">
        <span
          className={`font-bold ${row === null || row.rank === null ? 'text-muted-foreground' : 'text-primary'}`}
        >
          {place}
        </span>
        {row !== null && row.value > 0 && (
          <span className="text-muted-foreground">{Math.round(row.value).toLocaleString()}</span>
        )}
      </dd>
    </div>
  );
}

/**
 * What a guest sees instead of five empty rows.
 *
 * One sentence and one link. It is the only thing on this panel that asks the player for
 * anything, and it is here because it is the one moment they have just been given a reason to
 * care where they came.
 */
function RegisterPrompt({ className }: { className: string }) {
  return (
    <section
      data-testid="standing-register-prompt"
      className={`rounded-lg border border-dashed border-primary/40 bg-primary/5 p-2 text-left ${className}`}
    >
      <p className="text-[11px] text-muted-foreground sm:text-xs">
        Playing as a guest — this run counts for nothing.{' '}
        {/* A plain anchor rather than a router `Link`: this panel is drawn inside the arena
            overlay, which is the one subtree in the client that has to be renderable on its own,
            and following it tears the arena down whichever kind of navigation it is. */}
        <a href="/register" className="font-semibold text-primary hover:underline">
          Create an account
        </a>{' '}
        to be ranked on the boards.
      </p>
    </section>
  );
}
